import React, { useMemo } from 'react';
import { Navigate } from 'react-router-dom';

import { BlockFormConfirmSoftBlock } from '@eika-kortbetjening/block-form-confirm-soft-block';
import { SoftBlockLoader } from '@eika-kortbetjening/block-form-confirm-soft-block/lib/types';

import SkeletonList from '@/components/skeleton/SkeletonList';
import piwik from '@/piwik/piwik';
import { useSoftBlockCard } from '@/react-query/hooks/mutation/use-block-card';
import { useCard } from '@/react-query/hooks/query/use-card';
import { useCustomer } from '@/react-query/hooks/query/use-customer';
import { isHardBlocked, isSoftBlocked } from '@/utils/card-status-utils';
import { navigateToCardDetails } from '@/utils/url/get-card-details-url-utils';
import { getRootUrl, navigateToHardBlock } from '@/utils/url/get-url-utils';

import { CardIsAlreadySoftBlockedErrorCard } from '@components/error/ErrorCard';
import { Format, useLinkProducer, useNavigate } from '@domain/app';
import useText from '@hooks/use-text';
import { formatAccountNumber, isUser } from '@utils/account-utils';
import { canSoftBlock } from '@utils/allowed-operations/card-allowed-operations-utils';

const SoftBlockConfirm = () => {
    const text = useText();
    const { data: card, isPending } = useCard();
    const {
        mutate: softBlock,
        isPending: isSoftBlockingCard,
        isSuccess: hasSoftBlockedCard,
        isError: failedSoftBlockingCard,
    } = useSoftBlockCard();
    const navigate = useNavigate();
    const linkProducer = useLinkProducer();

    const softBlockLoader: SoftBlockLoader = useMemo(
        () => ({
            isSoftBlockingCard,
            hasSoftBlockedCard,
            failedSoftBlockingCard,
        }),
        [isSoftBlockingCard, hasSoftBlockedCard, failedSoftBlockingCard],
    );
    const { data: customer } = useCustomer(card);
    const user = isUser(customer);

    if (typeof card === 'undefined' || isPending) {
        return <SkeletonList />;
    }

    if (!hasSoftBlockedCard && isSoftBlocked(card)) {
        return <CardIsAlreadySoftBlockedErrorCard goToHardBlock={() => navigateToHardBlock(navigate)(card)} />;
    }

    if (isHardBlocked(card) || (!canSoftBlock(card) && !hasSoftBlockedCard)) {
        return <Navigate to={getRootUrl(linkProducer)} replace />;
    }

    const submit = () => {
        softBlock({ cardType: card.type, cardId: card.id });
    };

    const cancel = () => {
        navigateToCardDetails(navigate)(card);
    };

    const formattedPhoneNumber = user?.contact.mobile ? Format.phoneNumber(user.contact.mobile) : undefined;

    return (
        <BlockFormConfirmSoftBlock
            formattedCardNumber={Format.cardNumber(card.number)}
            formattedAccountNumber={formatAccountNumber(card.accountNumber)}
            formattedPhoneNumber={formattedPhoneNumber}
            submit={submit}
            cancel={cancel}
            softBlockLoader={softBlockLoader}
            sitecoreDictionary={text}
            piwik={piwik}
        />
    );
};

export default SoftBlockConfirm;
